import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  TextField,
  IconButton,
  Avatar,
  Button,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
// import AttachFileIcon from '@mui/icons-material/AttachFile';
// import CloseIcon from '@mui/icons-material/Close';
import styles from './WizardPage.module.css';
import WizardForm from 'components/Wizard/WizardForm';
import ChatHistory from 'components/Wizard/ChatHistory';
// import DocumentsSidebar from 'components/DocumentSideBar';
import SourceDetailModal from 'components/SourceModal/SourceModal';
import LAPSourceModal from '../LAPDetailPage/LAPSourceModal';
import EditIcon from '@mui/icons-material/Edit';
import { Divider } from '@mui/material';
import default_theme from 'assets/themes/themes';
import DeleteThreadDialog from 'components/Threads/DeleteThread';
import { fetchThreadsRequest, createThreadRequest, createThreadSuccess, deleteThreadRequest } from '../../../redux/threads/threadsSlice';
// import { fetchFilesRequest, uploadFileRequest, getFileUrl, getFileDownloadUrl } from '../../../redux/files/filesSlice';
import { fetchMessagesRequest } from '../../../redux/messages/messagesSlice';
import { sendMessageRequest, setCurrentThread } from '../../../redux/chatbot/chatbotSlice';

const WizardPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Redux state
  const threadsState = useSelector((state) => state.threads);
  // const filesState = useSelector((state) => state.files);
  const messagesState = useSelector((state) => state.messages);
  const chatbotState = useSelector((state) => state.chatbot);
  
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [formData, setFormData] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceModalOpen, setSourceModalOpen] = useState(false);
  const [selectedLAPSource, setSelectedLAPSource] = useState(null);
  const [lapModalOpen, setLapModalOpen] = useState(false);
  // const [selectedFiles, setSelectedFiles] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState(null);

  const [loadingText, setLoadingText] = useState('Thinking...');
  
  // Get userId and authentication status from auth state
  const authState = useSelector((state) => state.auth);
  const userId = authState?.user?.id ? String(authState.user.id) : null;
  const isAuthenticated = authState?.isAuthenticated ?? false;
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const activeRequestsRef = useRef(new Map());
  const formContextUsedRef = useRef(false);
  const pendingMessageRef = useRef(null);
  const waitingForThreadRef = useRef(null); // Track which thread we're waiting for
  const existingThreadIdsRef = useRef(new Set()); // Track thread IDs that existed when we started waiting
  const hasOptimisticMessagesRef = useRef(false); // Track if we have optimistically added messages
  const loadingThreadIdRef = useRef(null); // Track which thread ID is currently loading
  
  // Derived state from Redux
  const savedChats = threadsState.threads || [];
  const threadsLoading = threadsState.loading;
  // const documents = currentChatId ? (filesState.files[currentChatId] || []) : [];
  // const documentsLoading = filesState.loading;
  // const uploadingFiles = filesState.uploading;

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  
  useEffect(() => { 
    scrollToBottom(); 
  }, [messages]);

  useEffect(() => {
    dispatch(fetchThreadsRequest());
  }, [dispatch]);
  
  // Sync currentChatId with Redux chatbotState.currentThreadId
  useEffect(() => {
    if (currentChatId !== chatbotState.currentThreadId) {
      dispatch(setCurrentThread(currentChatId));
    }
  }, [currentChatId, chatbotState.currentThreadId, dispatch]);

  // Track when a new thread is created while we're waiting for one
  useEffect(() => {
    if (waitingForThreadRef.current === 'pending' && threadsState.threads?.length > 0) {
      const newThread = threadsState.threads.find(thread => 
        !existingThreadIdsRef.current.has(thread.id)
      );
  
      if (!currentChatId && newThread) {
        waitingForThreadRef.current = newThread.id;
      }
    }
  }, [threadsState.threads, currentChatId]);

  // Handle thread creation success and send pending message
  useEffect(() => {
    if (!waitingForThreadRef.current || waitingForThreadRef.current === 'pending' || !pendingMessageRef.current) {
      return;
    }

    const targetThread = threadsState.threads?.find(
      thread => thread.id === waitingForThreadRef.current
    );

    if (targetThread && pendingMessageRef.current && !currentChatId) {
      // A new thread was created and we have a pending message
      const pendingMsg = pendingMessageRef.current;
      pendingMessageRef.current = null;
      const threadIdToUse = waitingForThreadRef.current;
      waitingForThreadRef.current = null;
      existingThreadIdsRef.current.clear(); // Clear existing threads tracking
      
      // Set the new thread as current
      setCurrentChatId(threadIdToUse);
      dispatch(setCurrentThread(threadIdToUse));
      
      // Now send the pending message with the threadId - CRITICAL: always include threadId
      setTimeout(() => {
        dispatch(sendMessageRequest({
          message: pendingMsg.userMessage,
          files: pendingMsg.uploadedFileIds || [],
          metadata: pendingMsg.metadataPayload,
          threadId: threadIdToUse, // Include threadId in payload - CRITICAL
          ...(pendingMsg.includeFormContext && {
            context: pendingMsg.formContextPayload
          })
        }));
      }, 100);
    }
  }, [threadsState.threads, currentChatId, dispatch]);

  // Update savedChats when threads are loaded
  useEffect(() => {
    // Threads are already in Redux state, no need to update local state
  }, [threadsState.threads]);

  // Load documents when currentChatId changes
  // useEffect(() => {
  //   if (currentChatId) {
  //     dispatch(fetchFilesRequest({ threadId: currentChatId }));
  //   }
  // }, [currentChatId, dispatch]);
  
  // Update documents when files are loaded
  // useEffect(() => {
  //   // Documents are already in Redux state, accessed via useSelector
  // }, [filesState.files]);

  const currentChatIdRef = useRef(currentChatId);
  useEffect(() => {
    currentChatIdRef.current = currentChatId;
  }, [currentChatId]);

  // Get current thread's messages using useMemo to avoid unnecessary re-renders
  const currentThreadMessages = React.useMemo(() => {
    return currentChatId ? (messagesState.messages[currentChatId] || []) : [];
  }, [currentChatId, messagesState.messages]);

  useEffect(() => {
    // Only update if this is still the current thread
    if (currentChatId && currentChatIdRef.current === currentChatId) {
      if (currentThreadMessages && Array.isArray(currentThreadMessages) && currentThreadMessages.length > 0) {
        
        // Strictly trust the Redux state! No more aggressive deduplication.
        // We ensure they are sorted chronologically by timestamp just in case.
        const sortedMessages = [...currentThreadMessages].sort((a, b) => 
          new Date(a.timestamp) - new Date(b.timestamp)
        );
        
        setMessages(sortedMessages);
        hasOptimisticMessagesRef.current = false;
        
      } else {
        if (!hasOptimisticMessagesRef.current && !pendingMessageRef.current && !waitingForThreadRef.current) {
          setMessages([]);
        }
      }
    } else if (!currentChatId) {
      if (!pendingMessageRef.current && !waitingForThreadRef.current && !hasOptimisticMessagesRef.current) {
        setMessages([]);
      }
    }
  }, [currentChatId, currentThreadMessages]);

  // Sync local isLoading state with Redux chatbotState.loading, but only for current chat
  useEffect(() => {    
    if (currentChatId) {
      if (chatbotState.loading && chatbotState.currentThreadId === currentChatId) {
        setIsLoading(true);
        loadingThreadIdRef.current = currentChatId;
      } else {
        setIsLoading(false);
        if (chatbotState.loading && chatbotState.currentThreadId) {
          loadingThreadIdRef.current = chatbotState.currentThreadId;
        } else if (!chatbotState.loading && loadingThreadIdRef.current === currentChatId) {
          // Loading finished for this chat
          loadingThreadIdRef.current = null;
        }
      }
    } else {
      // No current chat - only show loading if we're creating a new thread
      if (chatbotState.loading && !chatbotState.currentThreadId && waitingForThreadRef.current) {
        setIsLoading(true);
      } else {
        setIsLoading(false);
        if (!chatbotState.loading) {
          loadingThreadIdRef.current = null;
        }
      }
    }
  }, [chatbotState.loading, chatbotState.currentThreadId, currentChatId]);

  // Looping loading messages to mimic the AI Agent's workflow
  useEffect(() => {
    let interval;
    
    if (isLoading) {
      const phrases = [
        "Thinking...",
        "Searching the solution database...",
        "Analyzing database results...",
        "Formatting the final answer...",
        "Refining insights...",
        "Almost there..."
      ];
      
      let currentIndex = 0;
      setLoadingText(phrases[0]);
      
      // Change the text every 12 seconds in a loop
      interval = setInterval(() => {
        currentIndex = (currentIndex + 1) % phrases.length;
        setLoadingText(phrases[currentIndex]);
      }, 12000);
      
    } else {
      setLoadingText("");
    }
    
    return () => clearInterval(interval);
  }, [isLoading]);

  // const handleFileSelect = (event) => {
  //   const files = Array.from(event.target.files);
  //   setSelectedFiles(prev => [...prev, ...files]);
  // };

  // const handleRemoveFile = (index) => {
  //   setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  // };

  // const uploadFiles = async (files, threadId) => {
  //   const uploadedFileIds = [];
    
  //   for (const file of files) {
  //     try {
  //       dispatch(uploadFileRequest({ file, threadId }));
  //     } catch (error) {
  //       console.error(`Error uploading file ${file.name}:`, error);
  //       throw error;
  //     }
  //   }

  //   return uploadedFileIds;
  // };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    if (!isAuthenticated || !userId) {
      navigate('/login');
      return;
    }

    const userMessage = inputMessage.trim();
    setInputMessage('');

    const includeFormContext = Boolean(formData && !formContextUsedRef.current);
    const formContextPayload = includeFormContext
      ? {
          goals: formData.goals,
          challenges: formData.challenges,
          resources: formData.resources,
          description: formData.description,
          relatedFields: Array.isArray(formData.relatedFields) ? [...formData.relatedFields] : [],
        }
      : null;

    // --- NEW CHAT LOGIC ---
    if (!currentChatId) {
      // 1. Save the message to pending so the useEffect can send it later
      pendingMessageRef.current = {
        userMessage,
        uploadedFileIds: [],
        metadataPayload: includeFormContext ? { form_context: formContextPayload } : null,
        includeFormContext,
        formContextPayload
      };
      
      // 2. Set flags to wait for thread creation
      waitingForThreadRef.current = 'pending';
      if (threadsState.threads) {
        existingThreadIdsRef.current = new Set(threadsState.threads.map(t => t.id));
      } else {
        existingThreadIdsRef.current = new Set();
      }
      
      // 3. Immediately show the user's message in the UI (Optimistic rendering)
      hasOptimisticMessagesRef.current = true;
      setMessages([{
        id: Date.now(),
        text: userMessage,
        sender: 'user',
        files: [],
        ...(includeFormContext && { metadata: { form_context: formContextPayload } })
      }]);
      
      setIsLoading(true);
      if (includeFormContext) formContextUsedRef.current = true;
      
      // 4. Dispatch thread creation to the backend (This generates the sidebar history!)
      dispatch(createThreadRequest({ 
        title: userMessage.length > 40 ? userMessage.substring(0, 40) + '...' : userMessage 
      }));
      
      return; // Stop here! The useEffect we left alone earlier will automatically send the chat once the DB confirms creation.
    }

    // --- EXISTING CHAT LOGIC ---
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        text: userMessage,
        sender: 'user',
        files: [],
        ...(includeFormContext && {
          metadata: { form_context: formContextPayload },
        }),
      },
    ]);

    if (includeFormContext) {
      formContextUsedRef.current = true;
    }

    setIsLoading(true);

    dispatch(sendMessageRequest({
      message: userMessage,
      threadId: currentChatId, // Use the existing DB thread
      context: includeFormContext ? formContextPayload : null,
      metadata: includeFormContext
        ? { form_context: formContextPayload }
        : null,
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = () => {
    activeRequestsRef.current.clear();
    pendingMessageRef.current = null;
    waitingForThreadRef.current = null;
    existingThreadIdsRef.current.clear(); 
    hasOptimisticMessagesRef.current = false; 
    
    setMessages([]);
    setCurrentChatId(null);
    dispatch(setCurrentThread(null)); // Explicitly clear Redux thread ID
    setShowForm(true);
    setFormData(null);
    // setSelectedFiles([]);
    // Documents are automatically cleared when currentChatId is null (derived from Redux state)
    setIsLoading(false);
    formContextUsedRef.current = false;
  };

  const loadChat = async (chatId) => {
    activeRequestsRef.current.clear();
    pendingMessageRef.current = null; 
    waitingForThreadRef.current = null; 
    existingThreadIdsRef.current.clear(); 
    hasOptimisticMessagesRef.current = false; 
    
    // CRITICAL: Clear loading FIRST before changing chat ID
    // This prevents loading bubble from showing in the new chat
    setIsLoading(false);
    
    setMessages([]);
    setCurrentChatId(chatId);
    setShowForm(false);
    
    formContextUsedRef.current = true;

    dispatch(fetchMessagesRequest({ threadId: chatId }));
  };



  const handleFormSubmit = (data) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setFormData(data);
    setShowForm(false);
    formContextUsedRef.current = false;
  };

  const handleFormCancel = () => {
    setShowForm(false);
  };

  const deleteChat = async (chatId, e) => {
    e.stopPropagation();
    
    // Find the thread to get its title
    const thread = savedChats.find(c => c.id === chatId);
    
    // Set the thread to delete and open the confirmation dialog
    setThreadToDelete({ id: chatId, title: thread?.title || 'Untitled Chat' });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!threadToDelete) return;

    try {
      dispatch(deleteThreadRequest({ threadId: threadToDelete.id }));
      
      if (currentChatId === threadToDelete.id) {
        setCurrentChatId(null);
        setMessages([]);
        hasOptimisticMessagesRef.current = false; // Clear optimistic messages flag
        setShowForm(true);
      }
      
      setDeleteDialogOpen(false);
      setThreadToDelete(null);
    } catch (error) {
      console.error('Error deleting thread:', error);
      setDeleteDialogOpen(false);
      setThreadToDelete(null);
    }
  };


  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setThreadToDelete(null);
  };

  const handleFormEdit = () => {
    setShowForm(true);
  };

  // const handleOpenDocument = (doc) => {
  //   window.open(getFileUrl(doc.file_id), '_blank');
  // };

  // const handleDownloadDocument = (doc) => {
  //   const link = document.createElement('a');
  //   link.href = getFileDownloadUrl(doc.file_id);
  //   link.download = doc.title;
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  const handleSourceClick = (source) => {
    // Check if this is a LAP source - LAP sources use UUID/title, NOT entry_id
    const sourceType = source.source_type || source._fullMetadata?.source;
    const catalogId = source.catalog_id || source._fullMetadata?.catalog_id;
    const hasEntryId = !!source.entry_id; // Solution catalogs have entry_id
    const hasLAPId = !!(source._fullMetadata?.id || source.id); // LAP sources have UUID id
    const hasLAPTitle = !!(source.title && /^[A-Z]\d+-LAP-/i.test(source.title)); // LAP title pattern like D1-LAP-S1
    
    // LAP sources can be identified by:
    // 1. source_type === 'lap' or _fullMetadata.source === 'lap'
    // 2. _fullMetadata.collection_type === 'lap'
    // 3. Has UUID/id but NO entry_id (LAPs use UUID, catalogs use entry_id)
    // 4. Title matches LAP pattern (e.g., D1-LAP-S1)
    // 5. catalog_id contains 'LAP'
    const isLAPSource = sourceType === 'lap' ||
                        source._fullMetadata?.source === 'lap' ||
                        source._fullMetadata?.collection_type === 'lap' ||
                        (hasLAPId && !hasEntryId) || // Has UUID but no entry_id = LAP
                        hasLAPTitle || // Title matches LAP pattern
                        (catalogId && (
                          catalogId === 'LAP Catalog' ||
                          catalogId.toString().toUpperCase().includes('LAP') ||
                          /^[A-Z]\d+-LAP-/i.test(catalogId.toString())
                        ));
    
    // Check if it's clearly a solution catalog source
    // Solution catalogs have entry_id and specific fields like story/impacts
    const isSolutionCatalog = hasEntryId && (source.story || source.impacts || source.challenges);
    
    // LAP sources: use UUID/title, open LAP modal
    if (isLAPSource || (hasLAPId && !hasEntryId)) {
      setSelectedLAPSource(source);
      setLapModalOpen(true);
      return;
    }
    
    // Solution catalog sources: have entry_id, open regular modal
    if (hasEntryId || isSolutionCatalog) {
      setSelectedSource(source);
      setSourceModalOpen(true);
      return;
    }
    
    // Fallback for sources with full_metadata but no clear identifier
    if (source.full_metadata) {
      setSelectedSource(source);
      setSourceModalOpen(true);
      return;
    }
    
    // Handle URL or navigation
    if (source.url) {
      window.open(source.url, '_blank', 'noopener,noreferrer');
    } else if (source.navigation_link) {
      navigate(source.navigation_link);
    }
  };

  const renderSourceLinks = (sources = []) => {
    if (!sources || !Array.isArray(sources) || sources.length === 0) {
      return null;
    }
    
    const unique = Object.values(
      sources.reduce((acc, s) => {
        const k = s.source_reference?.entry_id || s.source_url || `${s.text || ''}-${s.source_reference?.organisation || ''}`;
        if (!acc[k]) acc[k] = s;
        return acc;
      }, {})
    );
    
    return (
      <Box sx={{ mt: 2, pt: 1, borderTop: `1px solid ${default_theme.palette.border.subtle}` }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
          Sources:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxWidth: '100%', overflow: 'hidden' }}>
          {unique.map((s, i) => {
            const sourceRef = s.source_reference || {};
            const fullMetadata = sourceRef._fullMetadata || s._fullMetadata;
            
            const handleClick = (e) => {
              e.preventDefault();
              e.stopPropagation();
              
              const sourceToPass = {
                ...sourceRef,
                title: sourceRef.title || fullMetadata?.title || sourceRef.solution_title || s.text,
                entry_id: sourceRef.entry_id,
                id: sourceRef.id || fullMetadata?.id || sourceRef._fullMetadata?.id,
                url: s.source_url || sourceRef.url,
                source_type: sourceRef.source_type || s.source_type,
                catalog_id: sourceRef.catalog_id || s.catalog_id || fullMetadata?.catalog_id,
                _fullMetadata: fullMetadata || sourceRef._fullMetadata || s._fullMetadata,
                summary: sourceRef.summary || s.summary,
                aim: sourceRef.aim || s.aim,
                location: sourceRef.location || s.location,
                brief_description: sourceRef.brief_description || fullMetadata?.brief_description,
                objectives_of_the_solution: sourceRef.objectives_of_the_solution || fullMetadata?.objectives_of_the_solution,
                related_challenges: sourceRef.related_challenges || fullMetadata?.related_challenges,
                geography_and_territorial_context: sourceRef.geography_and_territorial_context || fullMetadata?.geography_and_territorial_context,
                solution_title: sourceRef.solution_title,
                story: sourceRef.story,
                impacts: sourceRef.impacts,
                challenges: sourceRef.challenges,
              };
              
              handleSourceClick(sourceToPass);
            };
            
            return (
              <Box
                key={i}
                onClick={handleClick}
                sx={{
                  cursor: 'pointer',
                  display: 'inline-block',
                  maxWidth: '100%',
                }}
              >
                <Chip
                  icon={<ChatBubbleOutlineIcon />}
                  label={sourceRef.solution_title || (s.text && !/^[A-Z]\d+-LAP-/i.test(s.text) ? s.text : null) || sourceRef.title || 'View Source'}
                  size="small"
                  clickable
                  onClick={handleClick}
                  className={styles.sourceChip}
                  sx={{
                    cursor: 'pointer',
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: { xs: '150px', sm: '200px', md: '250px' },
                    },
                    '&:hover': {
                      backgroundColor: default_theme.palette.other.main,
                      color: default_theme.palette.primary.main,
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  // const renderFileAttachments = (files) => {
  //   if (!files || !files.length) return null;
    
  //   return (
  //     <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
  //       {files.map((file, index) => (
  //         <Chip
  //           key={index}
  //           label={file.filename}
  //           size="small"
  //           icon={<AttachFileIcon />}
  //           sx={{ fontSize: '0.75rem' }}
  //         />
  //       ))}
  //     </Box>
  //   );
  // };

  const renderFormContext = (context) => {
    if (!context) return null;

    const infoItems = [
      { label: 'Goals', value: context.goals },
      { label: 'Challenges', value: context.challenges },
      { label: 'Resources', value: context.resources },
      { label: 'Description', value: context.description },
    ];

    const filteredItems = infoItems.filter((item) => item.value && item.value.trim().length > 0);
    const hasFields = Array.isArray(context.relatedFields) && context.relatedFields.length > 0;

    if (!filteredItems.length && !hasFields) {
      return null;
    }

    return (
      <Paper
        elevation={0}
        sx={{
          mt: 1.5,
          p: 1.5,
          bgcolor: default_theme.palette.primary.white,
          borderRadius: 2,
          border: `1px solid ${default_theme.palette.border.subtleLight}`,
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 1 }}>
          Project Context
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {filteredItems.map((item) => (
            <Typography key={item.label} variant="caption" sx={{ display: 'block' }}>
              <strong>{item.label}:</strong> {item.value}
            </Typography>
          ))}
          {hasFields && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              <strong>Fields:</strong> {context.relatedFields.join(', ')}
            </Typography>
          )}
        </Box>
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        height: 'calc(100vh - 145px)',
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
      }}
    >
      {isAuthenticated && (
        <ChatHistory
          savedChats={savedChats}
          currentChatId={currentChatId}
          onStartNewChat={startNewChat}
          onLoadChat={loadChat}
          onDeleteChat={deleteChat}
          loading={threadsLoading}
        />
      )}

      <Box sx={{ 
        flex: 1, 
        minWidth: 0,
        display: 'flex', 
        flexDirection: 'column', 
        mx: { xs: 0, md: 1 },
        height: '100%',
      }}>
        <Paper elevation={3} sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          borderRadius: { xs: 0, md: 2 }, 
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          <Box className={styles.chatHeader} sx={{ flexShrink: 0 }}>
            <Typography variant="h6" className={styles.headerTitle}>
              <ChatBubbleOutlineIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> AI Assistant
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={startNewChat}
              size="small"
              sx={{ display: { xs: 'flex', md: 'none' }, bgcolor: default_theme.palette.other.chatGreen, '&:hover': { bgcolor: default_theme.palette.other.chatGreenHover } }}
            >
              New
            </Button>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
            className={styles.scrollbar}
          >
                {showForm ? (
              <WizardForm 
                onFormSubmit={handleFormSubmit} 
                onCancel={handleFormCancel} 
                onEdit={handleFormEdit}
                isAuthenticated={isAuthenticated}
              />
            ) : (
              <Box sx={{ flex: 1, p: { xs: 1, md: 2 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {messages.length === 0 && (
                  <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 4, px: { xs: 1, md: 0 } }}>
                    <Typography variant="body1" gutterBottom>
                      Hello! I'm your RURACTIVE AI assistant. I can help you find information about:
                    </Typography>
                    <Typography variant="body2" component="div" sx={{ mt: 2 }}>
                      <ul style={{ textAlign: 'left', display: 'inline-block' }}>
                        <li>Rural development initiatives and solutions</li>
                        <li>Community resilience and adaptation strategies</li>
                        <li>Environmental protection and sustainability</li>
                        <li>Local governance and policy implementation</li>
                        <li>Innovation in rural areas</li>
                      </ul>
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                      Ask me about specific topics like "climate adaptation", "rural innovation", or "community resilience"!
                    </Typography>
                    {formData && (
                      <Paper sx={{ mt: 3, p: 2, bgcolor: default_theme.palette.other.backgroundGrey, textAlign: 'left', position: 'relative' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Your Project Context:
                          </Typography>
                          <Button
                            startIcon={<EditIcon />}
                            onClick={() => setShowForm(true)}
                            size="small"
                            sx={{
                              color: default_theme.palette.other.chatGreen,
                              '&:hover': { bgcolor: default_theme.palette.other.paleGreen }
                            }}
                          >
                            Edit
                          </Button>
                        </Box>
                        <Divider sx={{ mb: 1.5 }} />
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          <strong>Goals:</strong> {formData.goals.substring(0, 100)}{formData.goals.length > 100 ? '...' : ''}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          <strong>Challenges:</strong> {formData.challenges.substring(0, 100)}{formData.challenges.length > 100 ? '...' : ''}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          <strong>Resources:</strong> {formData.resources.substring(0, 100)}{formData.resources.length > 100 ? '...' : ''}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          <strong>Description:</strong> {formData.description.substring(0, 100)}{formData.description.length > 100 ? '...' : ''}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          <strong>Fields:</strong> {formData.relatedFields.join(', ')}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                )}
                {messages.map((message) => {
                  const isBot = message.sender === 'bot' || message.sender === 'assistant';
                  
                  return (
                  <Box
                    key={message.id}
                    className={
                      message.sender === 'user' ? styles.userMessage : message.isError ? styles.errorMessage : styles.botMessage
                    }
                  >
                    <Avatar
                      sx={{
                        bgcolor: message.sender === 'user' ? 'primary.main' : 'grey.400',
                        width: { xs: 28, md: 32 },
                        height: { xs: 28, md: 32 },
                        mr: 1,
                      }}
                    >
                      {message.sender === 'user' ? <PersonIcon /> : <ChatBubbleOutlineIcon />}
                    </Avatar>
                    <Box className={styles.messageContent}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.text}
                      </Typography>
                      {(message.metadata?.form_context || message.metadata?.context) && renderFormContext(message.metadata.form_context || message.metadata.context)}
                      {/* {message.files && renderFileAttachments(message.files)} */}
                      {isBot && !message.isError && renderSourceLinks(message.metadata?.knowledge_used || [])}
                    </Box>
                  </Box>
                );
                })}
                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1, mb: 1 }}>
                    <Box 
                      sx={{ 
                        p: 1.5, 
                        px: 2.5,
                        borderRadius: 2, 
                        backgroundColor: default_theme.palette.other.backgroundGrey || 'grey.100',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <CircularProgress size={16} color="inherit" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        {loadingText}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Box>
            )}
          </Box>

          {/* Input Area with File Upload */}
          <Box className={styles.inputContainer} sx={{ p: { xs: 1, md: 2 }, flexShrink: 0 }}>
            {/* Selected Files Preview */}
            {/* {selectedFiles.length > 0 && (
              <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedFiles.map((file, index) => (
                  <Chip
                    key={index}
                    label={file.name}
                    onDelete={() => handleRemoveFile(index)}
                    deleteIcon={<CloseIcon />}
                    size="small"
                    sx={{ maxWidth: '200px' }}
                  />
                ))}
              </Box>
            )} */}
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                value={inputMessage}
                onChange={(e) => {
                  if (!isAuthenticated) {
                    navigate('/login');
                    return;
                  }
                  setInputMessage(e.target.value);
                }}
                onKeyPress={handleKeyPress}
                placeholder={
                  !isAuthenticated
                    ? "Please sign in to start chatting..."
                    : (showForm ? "Please complete the form above to start chatting..." : "Type your message here...")
                }
                disabled={!isAuthenticated || isLoading || showForm}
                multiline
                minRows={1}
                maxRows={4}
                className={styles.messageInput}
                variant="outlined"
                fullWidth
                InputProps={{
                  style: {
                    background: showForm ? default_theme.palette.other.white : default_theme.palette.primary.main,
                    borderRadius: 8,
                  },
                }}
              />

              <IconButton
                onClick={sendMessage}
                disabled={!isAuthenticated || !inputMessage.trim() || isLoading || showForm}
                color={default_theme.palette.primary.black}
                className={styles.sendButton}
                aria-label="send"
                size="large"
              >
                {isLoading ? <CircularProgress size={24} /> : <SendIcon />}
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* <DocumentsSidebar
        documents={documents}
        onOpenDocument={handleOpenDocument}
        onDownloadDocument={handleDownloadDocument}
        loading={documentsLoading}
      /> */}

      <SourceDetailModal
        open={sourceModalOpen}
        onClose={() => {
          setSourceModalOpen(false);
          setSelectedSource(null);
        }}
        source={selectedSource}
      />

      <LAPSourceModal
        open={lapModalOpen}
        onClose={() => {
          setLapModalOpen(false);
          setSelectedLAPSource(null);
        }}
        source={selectedLAPSource}
      />

      <DeleteThreadDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        threadTitle={threadToDelete?.title || 'Untitled Chat'}
      />

      <style jsx>{`
        @keyframes typing {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-10px); }
        }
      `}</style>
    </Box>
  );
};

export default WizardPage;